<script setup>
import Layout from "../Layouts/Customer.vue";
import { computed, ref, onMounted, watch, reactive } from "vue";
import { Link, useForm, usePage } from "@inertiajs/inertia-vue3";
import { Inertia } from "@inertiajs/inertia";
import { useToast } from "primevue/usetoast";
import Toast from "primevue/toast";
import Dialog from "primevue/dialog";
import Button from "primevue/button";
import Dropdown from "primevue/dropdown";
import Calendar from "primevue/calendar";
import axios from "axios";

const props = defineProps([
    'customer',
    'menuItems',
    'flash',
    'device',
    'bookings',
    'success', // Success message from session
    'source', // Source page for back navigation
    'bookingStats' // Stats about used/allowed bookings
]);

// Initialize toast and dialog
const toast = useToast();
const deletingBooking = ref(false);
const confirmDialogVisible = ref(false);
const selectedBooking = ref(null);
const bookingDialogVisible = ref(false);
const bookingToEdit = ref(null);
const page = usePage();
const csrf_token = computed(() => page.props.value.csrf_token);

const download = () => {
    window.location.href = `/bookings/${props.customer.uuid}/${props.device.uuid}/download?source=${props.source || 'plan'}`;
};

const services = ref([]);
const availableProviders = ref([]);
const isSaving = ref(false);

const bookingForm = reactive({
    date: null,
    start: null,
    end: null,
    service_id: '',
    provider_id: '',
    resource_id: null
});

// Show success toast if we have a success message from server
onMounted(async () => {
    if (props.success) {
        toast.add({
            severity: 'success',
            summary: 'Erfolg',
            detail: props.success,
            life: 3000,
        });
    }
    
    // Also check for success message in session storage (from redirect)
    const sessionSuccess = sessionStorage.getItem('booking_delete_success') || sessionStorage.getItem('booking_create_success');
    if (sessionSuccess) {
        toast.add({
            severity: 'success',
            summary: 'Erfolg',
            detail: sessionSuccess,
            life: 3000,
        });
        // Clear the message so it doesn't show again on refresh
        sessionStorage.removeItem('booking_delete_success');
        sessionStorage.removeItem('booking_create_success');
    }

    // Fetch Services for this device
    try {
        const { data } = await axios.get(`/ajax/admin/services?device_id=${props.device.id}&all=1`);
        const allServices = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : []);
        services.value = allServices;
        // Don't auto-set here, let openDialog handle it when adding
    } catch (e) {
        console.error("Failed to load services", e);
    }
});

watch(() => bookingForm.service_id, async (newServiceId) => {
    if (!newServiceId) {
        availableProviders.value = [];
        bookingForm.provider_id = '';
        return;
    }
    
    // Fetch Providers for this service
    try {
        const { data } = await axios.get(`/ajax/admin/providers?service_id=${newServiceId}&all=1`);
        availableProviders.value = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : []);
        if (availableProviders.value.length > 0) {
            if (!bookingForm.provider_id || !availableProviders.value.some(p => p.uuid === bookingForm.provider_id)) {
                bookingForm.provider_id = availableProviders.value[0].uuid;
            }
        } else {
            bookingForm.provider_id = '';
        }
    } catch (e) {
        console.error("Failed to load providers for service", e);
        availableProviders.value = [];
        bookingForm.provider_id = '';
    }
});

const availableResources = ref([]);
const loadingResources = ref(false);

const fetchAvailableResources = async () => {
    if (!props.device?.allow_resource_selection) return;
    
    if (!bookingForm.service_id || !bookingForm.start) {
        availableResources.value = [];
        return;
    }
    
    let endDatetime = bookingForm.end;
    if (!endDatetime) {
        const service = services.value.find(s => s.uuid === bookingForm.service_id);
        if (service) {
            const start = typeof bookingForm.start === 'string' ? new Date(bookingForm.start.replace(' ', 'T')) : (bookingForm.start instanceof Date ? bookingForm.start : new Date(bookingForm.start));
            endDatetime = new Date(start.getTime() + ((service.duration || 30) * 60000));
        } else {
            return;
        }
    }

    loadingResources.value = true;
    try {
        const isoStart = formatDate(bookingForm.start);
        const isoEnd = formatDate(endDatetime);
        
        const params = { start_datetime: isoStart, end_datetime: isoEnd };
        if (bookingToEdit.value) {
            params.ignore_booking_id = bookingToEdit.value.id;
        }
        
        const { data } = await axios.get(`/ajax/admin/services/${bookingForm.service_id}/available-resources`, {
            params
        });
        const allResources = Array.isArray(data) ? data : (data.data || []);
        const resources = allResources.filter(r => r.available !== false);
        
        availableResources.value = [...resources];
        
        const isEdit = !!bookingToEdit.value;
        if (isEdit && bookingForm.resource_id && !resources.some(r => r.resource_id === bookingForm.resource_id)) {
            const existingRes = bookingToEdit.value?.resource_reservations?.find(r => r.device_resource_id === bookingForm.resource_id);
            if (existingRes) {
                availableResources.value.push({
                    resource_id: bookingForm.resource_id,
                    name: existingRes.device_resource?.name || existingRes.device?.name || 'Aktuelles Gerät',
                    available: true
                });
            } else {
                bookingForm.resource_id = resources.length >= 1 ? resources[0].resource_id : null;
            }
        } else if (!isEdit) {
            if (resources.length >= 1 && !bookingForm.resource_id) {
                bookingForm.resource_id = resources[0].resource_id;
            } else if (bookingForm.resource_id && !resources.some(r => r.resource_id === bookingForm.resource_id)) {
                bookingForm.resource_id = resources.length >= 1 ? resources[0].resource_id : null;
            }
        }
    } catch(e) {
        availableResources.value = [];
        console.error('Failed to fetch resources', e);
    } finally {
        loadingResources.value = false;
    }
};

watch([() => bookingForm.service_id, () => bookingForm.start, () => bookingForm.end], () => {
    fetchAvailableResources();
}, { deep: true });

// Format a Date object (from PrimeVue Calendar) to "YYYY-MM-DD HH:mm:ss" using
// LOCAL time methods only — never toISOString(), never UTC conversion.
// We force seconds to :00 so DayPilot doesn't push the block into the next 15-min slot.
const formatDate = (date) => {
  if (!date) return null;
  let d = date;
  if (typeof d === 'string') d = new Date(d.replace(/([+-]\d{2}:\d{2}|Z)$/, '').replace(' ', 'T'));
  else if (!(d instanceof Date)) d = new Date(d);
  if (isNaN(d.getTime())) return null;
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:00`;
};

const availableTimeSlots = ref([]);
const loadingSlots = ref(false);

const fetchAvailableSlots = async () => {
    if (!bookingForm.date || !bookingForm.service_id || !bookingForm.provider_id) {
        availableTimeSlots.value = [];
        return;
    }
    
    loadingSlots.value = true;
    try {
        const _d = bookingForm.date instanceof Date ? bookingForm.date : new Date(bookingForm.date);
        const datePart = _d.getFullYear() + '-' + String(_d.getMonth() + 1).padStart(2, '0') + '-' + String(_d.getDate()).padStart(2, '0');
        
        const service = services.value.find(s => s.uuid === bookingForm.service_id);
        const duration = service ? (service.duration || 30) : 30;

        const params = {
            service_id: bookingForm.service_id,
            provider_id: bookingForm.provider_id,
            date: datePart,
            step_minutes: 15,
            duration_minutes: duration
        };

        if (bookingToEdit.value) {
            params.ignore_booking_id = bookingToEdit.value.id;
        }

        const { data } = await axios.get('/ajax/admin/bookings/available-slots', { params });
        availableTimeSlots.value = Array.isArray(data?.data) ? data.data : [];
        
        if (!bookingToEdit.value && bookingForm.start) {
             const startStr = typeof bookingForm.start === 'string' ? bookingForm.start : formatDate(bookingForm.start);
             if (!availableTimeSlots.value.some(s => s.start === startStr)) {
                 bookingForm.start = null;
                 bookingForm.end = null;
             }
        }
    } catch (e) {
        console.error('Failed to fetch available slots', e);
        availableTimeSlots.value = [];
    } finally {
        loadingSlots.value = false;
    }
};

const formattedAvailableSlots = computed(() => {
    let slots = availableTimeSlots.value
        .filter(slot => slot.available !== false)
        .map(slot => {
        const startStr = String(slot.start).replace(' ', 'T');
        const endStr = String(slot.end).replace(' ', 'T');
        const startDate = new Date(startStr);
        const endDate = new Date(endStr);
        
        const pad = n => String(n).padStart(2, '0');
        const label = `${pad(startDate.getHours())}:${pad(startDate.getMinutes())} - ${pad(endDate.getHours())}:${pad(endDate.getMinutes())}`;
        return {
            ...slot,
            label,
            start: slot.start
        };
    });

    if (bookingToEdit.value && bookingForm.start) {
        const currentStartStr = typeof bookingForm.start === 'string' ? bookingForm.start : formatDate(bookingForm.start);
        if (!slots.some(s => s.start === currentStartStr)) {
            const startD = typeof bookingForm.start === 'string' ? new Date(bookingForm.start.replace(' ', 'T')) : bookingForm.start;
            let endD = bookingForm.end ? (typeof bookingForm.end === 'string' ? new Date(bookingForm.end.replace(' ', 'T')) : bookingForm.end) : null;
            
            const pad = n => String(n).padStart(2, '0');
            const startLabel = startD && !isNaN(startD.getTime()) ? `${pad(startD.getHours())}:${pad(startD.getMinutes())}` : '??:??';
            const endLabel = endD && !isNaN(endD.getTime()) ? `${pad(endD.getHours())}:${pad(endD.getMinutes())}` : '';
            
            slots.push({
                start: currentStartStr,
                end: bookingForm.end ? (typeof bookingForm.end === 'string' ? bookingForm.end : formatDate(bookingForm.end)) : null,
                label: endLabel ? `${startLabel} - ${endLabel}` : startLabel,
            });
            slots.sort((a, b) => a.start.localeCompare(b.start));
        }
    }
    return slots;
});

watch([() => bookingForm.date, () => bookingForm.service_id, () => bookingForm.provider_id], () => {
    fetchAvailableSlots();
}, { deep: true });

watch(() => bookingForm.start, (newStart) => {
    if (newStart) {
        const slot = formattedAvailableSlots.value.find(s => s.start === newStart);
        if (slot && slot.end) {
            bookingForm.end = slot.end;
        }
    }
});

const formattedBookings = computed(() => {
    if (!props.bookings || !Array.isArray(props.bookings)) {
        console.error('Bookings is not an array:', props.bookings);
        return [];
    }
    
    return props.bookings.map(booking => {
        try {
            // Raw string from server: "2026-06-24 23:15:00" (space separator, no timezone)
            // Split on either 'T' or space — handles both formats safely.
            const raw = booking.start || '';
            const sepIdx = raw.search(/[T ]/);
            const datePart = sepIdx > -1 ? raw.substring(0, sepIdx) : raw;
            const timePart = sepIdx > -1 ? raw.substring(sepIdx + 1) : '';
            // datePart: "2026-06-24"  →  "24.06.2026"
            const [year = '', month = '', day = ''] = datePart.split('-');
            const formattedDate = datePart ? `${day}.${month}.${year}` : 'N/A';
            // timePart: "23:15:00"  →  first 5 chars "23:15"
            const formattedTime = timePart ? timePart.substring(0, 5) : 'N/A';
            return {
                ...booking,
                formattedDate,
                formattedTime,
            };
        } catch (e) {
            console.error('Error formatting booking:', e, booking);
            return {
                ...booking,
                formattedDate: 'Error',
                formattedTime: 'Error'
            };
        }
    });
});

const openDialog = (booking = null) => {
    bookingToEdit.value = booking;
    
    // Helper: strip timezone offset and parse as local time
    const parseLocal = (str) => {
        if (!str) return null;
        const normalized = String(str).replace(/([+-]\d{2}:\d{2}|Z)$/, '').replace(' ', 'T');
        const d = new Date(normalized);
        return isNaN(d.getTime()) ? null : d;
    };

    if (booking) {
        bookingForm.service_id  = booking.service_uuid  || '';
        bookingForm.provider_id = booking.provider_uuid || '';
        bookingForm.start = booking.start;
        bookingForm.end   = booking.end;
        bookingForm.date  = parseLocal(booking.start);
        bookingForm.resource_id = booking.resource_reservations?.[0]?.device_resource_id || null;
    } else {
        bookingForm.date = null;
        bookingForm.start = null;
        bookingForm.end = null;
        bookingForm.service_id = services.value.length > 0 ? services.value[0].uuid : '';
        bookingForm.provider_id = '';
        bookingForm.resource_id = null;
    }
    
    bookingDialogVisible.value = true;
};

const closeDialog = () => {
    bookingDialogVisible.value = false;
    bookingToEdit.value = null;
};

const saveBooking = async () => {
    if (isSaving.value) return;

    if (!bookingForm.start || !bookingForm.service_id || !bookingForm.provider_id || (props.device?.allow_resource_selection && !bookingForm.resource_id)) {
        toast.add({ severity: 'warn', summary: 'Fehlende Daten', detail: 'Bitte füllen Sie alle Pflichtfelder aus.', life: 3000 });
        return;
    }

    isSaving.value = true;

    try {
        const payload = {
            provider_id: bookingForm.provider_id,
            service_id: bookingForm.service_id,
            start_datetime: formatDate(bookingForm.start),
        };
        
        if (bookingForm.end) {
            payload.end_datetime = formatDate(bookingForm.end);
        }
        if (props.device?.allow_resource_selection && bookingForm.resource_id) {
            payload.resource_id = bookingForm.resource_id;
        }

        if (bookingToEdit.value) {
            // Update
            await axios.patch(`/ajax/admin/bookings/${bookingToEdit.value.uuid}/reschedule`, payload);
            toast.add({ severity: 'success', summary: 'Erfolg', detail: 'Buchung erfolgreich aktualisiert.', life: 3000 });
        } else {
            // Create
            payload.customer_id = props.customer.id;
            payload.booking_source = 'admin';
            payload.type = 'plan';
            payload.device_id = props.device.id;
            
            await axios.post('/ajax/admin/bookings', payload, {
                headers: { 'X-Idempotency-Key': window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}` }
            });
            sessionStorage.setItem('booking_create_success', 'Buchung wurde erfolgreich hinzugefügt.');
        }

        bookingDialogVisible.value = false;
        bookingToEdit.value = null;
        Inertia.reload({ only: ['bookings', 'bookingStats', 'flash'] });

    } catch (e) {
        console.error('Save booking failed', e);
        const data = e.response?.data;
        let msg = 'Speichern fehlgeschlagen.';
        if (data?.errors) {
            msg = Object.values(data.errors).flat()[0] || msg;
        } else if (data?.message) {
            msg = data.message;
        }
        toast.add({ severity: 'error', summary: 'Fehler', detail: msg, life: 5000 });
    } finally {
        isSaving.value = false;
    }
};

const showDeleteConfirmDialog = (booking) => {
    selectedBooking.value = booking;
    confirmDialogVisible.value = true;
};

const deleteBooking = () => {
    if (deletingBooking.value || !selectedBooking.value) {
        return;
    }

    deletingBooking.value = true;
    confirmDialogVisible.value = false;
    
    // Store success message in session storage to show after redirect
    sessionStorage.setItem('booking_delete_success', 'Buchung wurde erfolgreich gelöscht.');
    
    // Build the delete URL with source parameter if present
    let deleteUrl = `/bookings/${selectedBooking.value.uuid}`;
    if (props.source) {
        deleteUrl += `?source=${props.source}`;
    }
    
    // Use Inertia.js directly to handle the deletion and redirect
    useForm().delete(deleteUrl, {}, {
        // Don't prevent the full page reload
        preserveState: false,
        onError: (errors) => {
            // Clear pending success message if there's an error
            sessionStorage.removeItem('booking_delete_success');
            
            toast.add({
                severity: 'error',
                summary: 'Fehler',
                detail: 'Die Buchung konnte nicht gelöscht werden.',
                life: 3000,
            });
            console.error(errors);
            deletingBooking.value = false;
            selectedBooking.value = null;
        }
    });
};

const cancelDelete = () => {
    confirmDialogVisible.value = false;
    selectedBooking.value = null;
};

</script>

<template>
    <Layout :customer="customer" :menu-items="menuItems" :flash="flash">
        <Dialog
            v-model:visible="bookingDialogVisible"
            :modal="true"
            :header="bookingToEdit ? 'Buchung bearbeiten' : 'Buchung hinzufügen'"
            :closable="false"
            :style="{ width: '550px' }"
        >
            <div class="confirmation-content space-y-4">
                <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">Kunde</label>
                    <div class="w-full bg-gray-50 border border-gray-300 rounded px-3 py-2 flex items-center gap-3">
                        <span class="font-bold text-gray-700">{{ customer.first_name }} {{ customer.last_name }}</span>
                    </div>
                </div>

                <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">Service *</label>
                    <Dropdown 
                        v-model="bookingForm.service_id" 
                        :options="services" 
                        optionLabel="name" 
                        optionValue="uuid" 
                        placeholder="Service wählen"
                        class="w-full !rounded-lg"
                    />
                </div>

                <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">Mitarbeiter *</label>
                    <Dropdown 
                        v-model="bookingForm.provider_id" 
                        :options="availableProviders" 
                        optionLabel="name" 
                        optionValue="uuid" 
                        placeholder="Mitarbeiter wählen"
                        class="w-full !rounded-lg"
                    />
                </div>

                 <div class="grid grid-cols-2 gap-4">
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-2">Datum *</label>
                        <Calendar 
                            v-model="bookingForm.date" 
                            dateFormat="dd.mm.yy" 
                            class="w-full"
                            inputClass="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-black outline-none"
                        />
                    </div>
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-2">Zeitfenster *</label>
                        <Dropdown 
                            v-model="bookingForm.start" 
                            :options="formattedAvailableSlots" 
                            optionLabel="label" 
                            optionValue="start" 
                            placeholder="Zeitfenster wählen"
                            :loading="loadingSlots"
                            :disabled="!bookingForm.date || !bookingForm.service_id || !bookingForm.provider_id"
                            class="w-full !rounded-lg"
                        />
                    </div>
                </div>

                <div v-if="props.device?.allow_resource_selection">
                    <label class="block text-sm font-medium text-gray-700 mb-2">Geräteressource *</label>
                    <Dropdown 
                        v-model="bookingForm.resource_id" 
                        :options="availableResources" 
                        optionLabel="name" 
                        optionValue="resource_id" 
                        placeholder="Ressource wählen"
                        :loading="loadingResources"
                        class="w-full !rounded-lg"
                    />
                </div>
            </div>
            <template #footer>
                <Button label="Abbrechen" icon="pi pi-times" class="p-button-text" @click="closeDialog" />
                <Button :label="bookingToEdit ? 'Aktualisieren' : 'Speichern'" icon="pi pi-check" @click="saveBooking" :loading="isSaving" />
            </template>
        </Dialog>
        
        <Dialog 
            v-model:visible="confirmDialogVisible" 
            :modal="true"
            header="Buchung löschen"
            :closable="false"
            :style="{ width: '450px' }">
            <div class="confirmation-content">
                <p class="mb-4">Sind Sie sicher, dass Sie diese Buchung löschen möchten?</p>
                <p class="mb-4" v-if="selectedBooking">
                    Datum: {{ selectedBooking.formattedDate }} um {{ selectedBooking.formattedTime }}
                </p>
            </div>
            <template #footer>
                <Button label="Nein" icon="pi pi-times" class="p-button-text" @click="cancelDelete" />
                <Button label="Ja" icon="pi pi-check" class="p-button-danger" @click="deleteBooking" :loading="deletingBooking" />
            </template>
        </Dialog>
        <div class="py-7 px-14">
            <div class="relative mb-9">
                <div class="flex items-start justify-between gap-4">
                    <div>
                        <h1 class="text-black text-2xl font-regular">
                            Buchungen für {{ device.name }}
                        </h1>
                        <div class="text-gray-500 text-sm mt-2">
                            Kunde: {{ customer.first_name }} {{ customer.last_name }}
                        </div>
                        <div class="text-gray-500 text-sm mt-1" v-if="bookingStats">
                            Gebuchte Termine: {{ bookingStats.used }}/{{ bookingStats.allowed }}
                        </div>
                    </div>
                    <div class="flex items-center gap-4">
                        <i class="pi pi-file-pdf text-[#C5C6CC] cursor-pointer text-2xl" @click="download" title="Als PDF herunterladen"></i>
                        <Button
                            label="Buchung hinzufügen"
                            icon="pi pi-plus"
                            class="p-button-sm"
                            @click="openDialog()"
                        />
                    </div>
                </div>
            </div>

            <div class="bg-white shadow-md rounded-lg overflow-hidden">
                <table class="min-w-full divide-y divide-gray-200">
                    <thead class="bg-gray-50">
                        <tr>
                            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Datum
                            </th>
                            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Uhrzeit
                            </th>
                            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Provider
                            </th>
                            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Service
                            </th>
                            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Typ
                            </th>
                            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Aktionen
                            </th>
                        </tr>
                    </thead>
                    <tbody class="bg-white divide-y divide-gray-200">
                        <tr v-if="formattedBookings.length === 0">
                            <td colspan="5" class="px-6 py-4 text-center text-sm text-gray-500">
                                Keine Buchungen gefunden
                            </td>
                        </tr>
                        <tr v-for="booking in formattedBookings" :key="booking.id" class="hover:bg-gray-50">
                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {{ booking.formattedDate }}
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {{ booking.formattedTime }}
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {{ booking.provider || 'N/A' }}
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {{ booking.service || 'N/A' }}
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {{ booking.type || 'N/A' }}
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                <div class="flex items-center gap-3">
                                    <button
                                        @click="openDialog(booking)"
                                        class="text-blue-600 hover:text-blue-800 focus:outline-none"
                                        :disabled="isSaving || deletingBooking"
                                        title="Buchung bearbeiten"
                                    >
                                        <i class="pi pi-file-edit text-base"></i>
                                    </button>
                                    <button
                                        @click="showDeleteConfirmDialog(booking)"
                                        class="text-red-600 hover:text-red-700 focus:outline-none"
                                        :disabled="deletingBooking || isSaving"
                                        title="Buchung löschen"
                                    >
                                        <i class="pi pi-trash text-base"></i>
                                    </button>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="mt-6" v-if="source">
                <Link v-if="source === 'fitness'" 
                      :href="`/customers/${customer.uuid}/fitness`" 
                      class="text-blue-600 hover:text-blue-900">
                    ← Zurück zu Fitness
                </Link>
                <Link v-else-if="source === 'packages'" 
                      :href="`/customers/${customer.uuid}/packages`" 
                      class="text-blue-600 hover:text-blue-900">
                    ← Zurück zu Pakete
                </Link>
                <Link v-else-if="source === 'plan'" 
                      :href="`/customers/${customer.uuid}/plan`" 
                      class="text-blue-600 hover:text-blue-900">
                    ← Zurück zum Behandlungsplan
                </Link>
            </div>
        </div>
    </Layout>
</template>